/**
 * 入住续住
 */
import React, { useEffect, useRef, memo } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Button } from 'antd';
import intl from 'react-intl-universal';

import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';
import { OrderDetailTabKey } from '@/pages/order/detail/component/DetailInfo';
import PreSettleDrawer, { PreSettleDrawerRef } from '@/components/Reservation/PreSettleDrawer';
import ThrottleButton from '@/components/Reservation/ThrottleButton';
import checkInService from '@/api/checkIn';
import useOrderLink from '@/hooks/useOrderLink';

import PopupTarget, { TargetType } from '@/components/Reservation/PopupTarget';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { initPageState, setLoadingStatus, getMainState } from '@/redux/actions/reservation/renewal';
import { ReducerState, storeName } from '@/redux/reducers/reservation/renewal';

import CustomerInfo from './components/CustomerInfo';
import RentTimeInfoNode from './components/RentTimeInfo';
import ChannelInfoNode from './components/ChannelInfo';
import RentRoomList from './components/RentRoomList';
import validator from './validator';
import * as helper from './helper';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading } = state;
  return { isInit, isLoading };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitState: initPageState,
  callSetLoading: setLoadingStatus,
  callGetState: getMainState,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IConfig {
  mainOrderId: string; // 订单id
  recList: Array<string>; // 接待单列表
  onEnd?: (result: boolean) => void;
}
interface IProps extends IConfig, MapStateProps, MapDispatchProps {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const { unmountNode, onEnd, isInit, isLoading, callSetLoading, callGetState } = props;

  const preSettleRef = useRef<PreSettleDrawerRef>(null);
  const { linkToReservationDetail } = useOrderLink();

  useEffect(() => {
    const { callInitState, mainOrderId, recList } = props;
    if (mainOrderId && recList && recList.length) {
      callInitState(() => {
        return helper.getStateFromOrder(mainOrderId, recList).catch((e) => {
          unmountNode();
          throw e;
        });
      });
    } else {
      message.error(intl.get('reservation_error_config_invalid'));
      unmountNode();
    }
  }, []);

  // 回调
  const closeCallback = (isModify: boolean) => {
    if (typeof onEnd === 'function') {
      onEnd(isModify);
    }
  };

  const handleEditCancel = () => {
    unmountNode();
    closeCallback(false);
  };

  // 预结算
  const showPreSettleDrawer = () => {
    callGetState((state) => {
      const orderNo = state.commonInfo.mainOrderId;
      preSettleRef.current.showDrawer(orderNo, () => {
        message.success(intl.get('drawer_extension_message_create_success'));
        unmountNode();
        linkToReservationDetail(orderNo, {
          tabKey: OrderDetailTabKey.RoomKey,
        });
        closeCallback(true);
      });
    });
  };

  const handleSubmitClick = () => {
    callGetState(async (state) => {
      const err = validator(state);
      if (err) {
        message.error(err);
        return;
      }
      callSetLoading(true);
      try {
        const requestData = helper.formatToSubmitData(state);
        await checkInService.submitRenewalInfo(requestData);
        showPreSettleDrawer();
      } catch (_) {
        callSetLoading(false);
      }
    });
  };

  if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader title={intl.get('drawer_extension_normal_title')} onClose={handleEditCancel} />
      <div className={styles.pageContainer}>
        <PopupTarget id={TargetType.Renewal} />
        <CustomerInfo />
        <RentTimeInfoNode />
        <ChannelInfoNode />
        <RentRoomList />
      </div>
      <DrawerFooter>
        <ThrottleButton type="primary" className={styles.submitBtn} onClick={handleSubmitClick}>
          {intl.get('drawer_extension_submit_btn')}
        </ThrottleButton>
        <Button onClick={handleEditCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
      <PreSettleDrawer ref={preSettleRef} />
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));

// export default () => {
//   const { showDrawer } = usePortalDrawer(Index);
//   useEffect(() => {
//     // { mainOrderId: 'Y20211222851901' }
//     showDrawer({ mainOrderId: 'Y20220106152068', recList: ['J20220106830063'] });
//   }, []);
//   return null;
// };
